import { asList, type LearningItem } from '@akademi/site-builder-runtime';
import type { CourseCardProps } from './CourseCard.types';

/**
 * Map a {@link LearningItem} from {@code LearningSource} to the
 * {@link CourseCardProps} shape every card variant accepts.
 *
 * <h3>URL construction</h3>
 *
 * The mapper produces hrefs from a template. Courses default to a
 * friendly path {@code /courses/{slug}-{id}} (e.g.
 * {@code /courses/scorm-2nd-test-26-mart-141351}); articles to
 * {@code /news/{id}}, events to {@code /events/{id}}. Operators can
 * override with an explicit {@code hrefTemplate} containing the
 * {@code {id}} and/or {@code {slug}} tokens.
 *
 * <p>Host owns routing entirely; the runtime stays tenant-agnostic
 * so the template is just a path with no tenant prefix.
 */

/**
 * Slugify a title into a URL-safe segment — diacritics stripped,
 * lowercased, non-alphanumerics collapsed to single hyphens. Mirrors
 * the host's {@code toFriendlyPath} so card links resolve to the same
 * canonical URLs the detail route builds.
 */
function slugifyTitle(title: string | undefined): string {
  return (title ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function learningItemToCardProps(
  item: LearningItem,
  opts: {
    kind?: 'course' | 'article' | 'event';
    hrefTemplate?: string;
    certificateBadgeUrl?: string;
  } = {},
): CourseCardProps {
  const kind = opts.kind ?? 'course';
  const defaultTemplate =
    kind === 'article'
      ? '/news/{id}'
      : kind === 'event'
        ? '/events/{id}'
        : '/lxp/course/{slug}-{id}';
  const template = opts.hrefTemplate ?? defaultTemplate;
  const slug = slugifyTitle(item.title) || 'course';
  const href = template
    .replace('{slug}', encodeURIComponent(slug))
    .replace('{id}', encodeURIComponent(item.id));

  // A missing OR zero/negative price is "free" — the marketplace card then
  // shows its "Ücretsiz" label instead of a "₺0,00".
  const price =
    typeof item.price === 'number' && item.price > 0
      ? `${item.priceCurrency ?? '£'}${item.price.toFixed(2)}`
      : undefined;

  // Same `{slug}-{id}` segment the href template resolves to, just under
  // the in-portal player route instead of the detail route — see
  // `getCoursePlayUrl` in the host's course-detail API module. Only
  // courses have a player.
  const playHref =
    kind === 'course'
      ? `/lxp/course/player/deliver/${encodeURIComponent(slug)}-${encodeURIComponent(item.id)}`
      : undefined;

  return {
    id: item.id,
    title: item.title,
    href,

    subtitle: item.subtitle,
    image: item.imageUrl,

    instructor: item.instructor,
    instructorAvatar: item.instructorAvatar,
    trainers: item.trainers,

    ratingAverage: item.ratingAverage,
    ratingCount: item.ratingCount,
    duration: item.duration,
    totalHours: item.totalHours,
    level: item.level,
    progress: item.progress,
    attendeeStatus: item.attendeeStatus,
    approvalRequired: item.approvalRequired,
    enrolled: item.enrolled,
    hasCertificate: item.hasCertificate,
    certificateBadgeUrl: item.certificateBadgeUrl ?? opts.certificateBadgeUrl,
    kicker: item.kicker,
    badge: item.badge,
    pluginId: item.pluginId,
    domain: item.domain,

    viewCount: item.viewCount,
    commentCount: item.commentCount,

    price,
    isBestseller: item.isBestseller,
    lastUpdated: item.lastUpdated,
    outcomes: asList<string>(item.outcomes).slice(0, 8),
    previewVideoUrl: item.previewVideoUrl,

    description: item.description,
    requirements: item.requirements,
    targetAudience: item.targetAudience,
    chapters: item.chapters,

    playHref,
    liveStartDate: item.liveStartDate,
    liveEndDate: item.liveEndDate,
    publicJoinUrl: item.publicJoinUrl,
  };
}
